import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="enrollment-fields"
export default class extends Controller {
  static targets = [
    "packages",
    "packageCard",
    "mainCard",
    "includeWebinarConsultationField",
    "depositOnlyField",
    "paymentPlanField",
    "generatedLink",
    "includeWebinarCheckbox",
    "depositOnlyCheckbox",
    "hiddenField",
    "paymentPlanFeeWaivedField",
    "paymentPlanFeeCheckbox",
  ];

  connect() {
    const params = new URLSearchParams(window.location.search);

    this.applyPackages(params);
    this.applyWebinar(params);
    this.applyDepositOnly(params);
    this.applyPaymentPlan(params);
    this.applyPaymentPlanFee(params);
    this.syncInitialVisibility();

    const preselectedId = this.hasHiddenFieldTarget
      ? this.hiddenFieldTarget.value
      : null;

    if (preselectedId) {
      this.dispatch("active-changed", { detail: { id: preselectedId } });
      return;
    }

    const visible = this.packagesTargets.filter((x) => x.checked);
    if (visible.length > 0) {
      this.dispatch("active-changed", { detail: { id: visible[0].value } });
    }
  }

  syncInitialVisibility() {
    this.packagesTargets.forEach((checkbox) => {
      const packageId = checkbox.value;
      const card = this.packageCardTargets.find(
        (x) => x.dataset.enrollmentFieldsPackageId === packageId,
      );
      const mainCard = this.mainCardTargets.find(
        (x) => x.dataset.enrollmentFieldsPackageId === packageId,
      );

      const isVisible = checkbox.checked ? "true" : "false";

      if (card) {
        card.dataset.enrollmentVisible = isVisible;
      }
      if (mainCard) {
        mainCard.dataset.enrollmentVisible = isVisible;
      }
    });

    this.dispatch("visibility-changed");
  }

  handleWebinarField(event) {
    const div = this.includeWebinarConsultationFieldTarget;
    div.classList.toggle("hidden", !event.target.checked);
    div.querySelector("input[type='checkbox']").required = event.target.checked;
  }

  handleDepositOnlyField(event) {
    this.depositOnlyFieldTarget.value = event.target.checked ? "true" : "false";
  }

  handlePaymentOption(event) {
    this.paymentPlanFieldTarget.value = event.target.value;
  }

  handlePaymentPlanFeeField(event) {
    this.paymentPlanFeeWaivedFieldTarget.value = event.target.checked
      ? "false"
      : "true";
  }

  togglePackages(event) {
    const checkbox = event.target;
    const packageId = checkbox.value;
    const card = this.packageCardTargets.find(
      (x) => x.dataset.enrollmentFieldsPackageId === packageId,
    );
    const mainCard = this.mainCardTargets.find(
      (x) => x.dataset.enrollmentFieldsPackageId === packageId,
    );

    const checked = this.packagesTargets.filter((x) => x.checked);
    const checkedCount = checked.length;

    if (checkedCount === 1) {
      this.dispatch("active-changed", { detail: { id: checked[0].value } });
    }

    if (!checkbox.checked && checkedCount >= 2) {
      this.dispatch("active-changed", { detail: { id: checked[0].value } });
    }

    if (card && checkedCount > 0) {
      const isVisible = checkbox.checked ? "true" : "false";
      card.dataset.enrollmentVisible = isVisible;
      card.classList.toggle("hidden", !checkbox.checked);

      if (mainCard) {
        mainCard.dataset.enrollmentVisible = isVisible;
      }

      this.dispatch("visibility-changed");
    } else {
      checkbox.checked = true;
      card.dataset.enrollmentVisible = "true";
      if (mainCard) {
        mainCard.dataset.enrollmentVisible = "true";
      }
    }
  }

  // READ PARAMS

  buildParams() {
    const params = new URLSearchParams();

    this.packagesTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        params.append("packages[]", checkbox.value);
      }
    });

    if (this.includeWebinarCheckboxTarget.checked) {
      params.set("include_webinar", "1");
    }

    if (this.depositOnlyCheckboxTarget.checked) {
      params.set("deposit_only", "1");
    }

    if (this.paymentPlanFieldTarget.value) {
      params.set("payment_plan", this.paymentPlanFieldTarget.value);
    }

    if (!this.paymentPlanFeeCheckboxTarget.checked) {
      params.set("payment_plan_fee_waived", "1");
    }

    return params;
  }

  save() {
    const params = this.buildParams();

    const newUrl =
      params.toString().length > 0
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;

    history.replaceState({}, "", newUrl);
  }

  applyPackages(params) {
    const allowed = params.getAll("packages[]");

    if (allowed.length === 0) return;

    this.packagesTargets.forEach((checkbox) => {
      const visible = allowed.includes(checkbox.value);
      checkbox.checked = visible;

      const card = this.packageCardTargets.find(
        (x) => x.dataset.enrollmentFieldsPackageId === checkbox.value,
      );

      if (!card) return;

      card.dataset.enrollmentVisible = visible ? "true" : "false";
      card.classList.toggle("hidden", !visible);
    });

    this.dispatch("visibility-changed");
  }

  applyWebinar(params) {
    if (params.get("include_webinar") !== "1") return;

    const field = this.includeWebinarConsultationFieldTarget;
    const checkbox = this.includeWebinarCheckboxTarget;
    checkbox.checked = true;
    const field_checkbox = field.querySelector("input[type='checkbox']");
    field_checkbox.required = true;
    field.classList.remove("hidden");
  }

  applyDepositOnly(params) {
    if (params.get("deposit_only") != "1") return;

    this.depositOnlyFieldTarget.value = "true";

    const checkbox = this.depositOnlyCheckboxTarget;

    checkbox.checked = true;
  }

  applyPaymentPlan(params) {
    const plan = params.get("payment_plan");
    if (!plan) return;

    this.paymentPlanFieldTarget.value = plan;

    const radio = this.element.querySelector(
      `input[type="radio"][name="payment_option"][value="${plan}"]`,
    );

    if (radio) radio.checked = true;
  }

  applyPaymentPlanFee(params) {
    if (params.get("payment_plan_fee_waived") !== "1") return;

    this.paymentPlanFeeWaivedFieldTarget.value = "true";
    this.paymentPlanFeeCheckboxTarget.checked = false;
  }

  generateLink() {
    const params = this.buildParams();

    const baseUrl = `${window.location.origin}${window.location.pathname}`;
    const url =
      params.toString().length > 0
        ? `${baseUrl}?${params.toString()}`
        : baseUrl;

    this.generatedLinkTarget.value = url;
    this.generatedLinkTarget.select();
  }

  copyLink(event) {
    if (!this.generatedLinkTarget.value) return;

    navigator.clipboard.writeText(this.generatedLinkTarget.value);

    const button = event.currentTarget;
    const icon = button.querySelector("i");

    icon.style.display = "none";
    const copiedText = document.createElement("span");
    copiedText.innerText = "Copied!";
    copiedText.classList.add("text-sm", "text-blue-600");
    button.appendChild(copiedText);

    setTimeout(() => {
      copiedText.remove();
      icon.style.display = "";
    }, 1200);
  }
}
